import { FC } from "react";
import {
  Badge,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useAsyncRetry } from "react-use";
import { TrackedSerialNumbersService } from "../api/generated/TrackedSerialNumbersService";
import { renderedDateFormat } from "../utils/luxon";
import { TrackedSerialNumberAuditDisplayDto } from "../api/generated/index.defs.ts";

// Map change types to display colors
const changeTypeColorMap: Record<string, string> = {
  Created: "green",
  Updated: "blue",
  RigAssigned: "teal",
  RigUnassigned: "orange",
  Synced: "cyan",
  CosDateOverrideSet: "violet",
  CosDateOverrideCleared: "gray",
};

// Format field names for display
const formatFieldName = (fieldName: string): string => {
  // Convert camelCase to Title Case with spaces
  return fieldName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Format change type for display
const formatChangeType = (changeType: string): string => {
  // Add spaces before capital letters
  return changeType.replace(/([A-Z])/g, " $1").trim();
};

type AuditHistoryRowProps = {
  audit: TrackedSerialNumberAuditDisplayDto;
};

const AuditHistoryRow: FC<AuditHistoryRowProps> = ({ audit }) => {
  const color = changeTypeColorMap[audit.changeType] || "gray";

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm">{renderedDateFormat(audit.createdDate!)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{audit.createdByUserName || "System"}</Text>
      </Table.Td>
      <Table.Td>
        <Badge color={color} variant="light" size="sm">
          {formatChangeType(audit.changeType)}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatFieldName(audit.fieldName)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {audit.oldValue || "-"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={500}>
          {audit.newValue || "-"}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
};

type TrackedSnAuditHistoryModalProps = {
  opened: boolean;
  onClose: () => void;
  trackedSerialNumberId: number | undefined;
  serialNumber: string | undefined;
};

export const TrackedSnAuditHistoryModal: FC<
  TrackedSnAuditHistoryModalProps
> = ({ opened, onClose, trackedSerialNumberId, serialNumber }) => {
  const fetchAuditHistory = useAsyncRetry(async () => {
    if (!trackedSerialNumberId) return [];

    const response = await TrackedSerialNumbersService.getAuditHistory({
      id: trackedSerialNumberId,
    });

    if (response.hasErrors) {
      // eslint-disable-next-line no-restricted-syntax
      console.error("Failed to fetch audit history:", response.errors);
      return [];
    }

    return response.data || [];
  }, [trackedSerialNumberId]);

  const auditHistory = fetchAuditHistory.value || [];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Title order={4}>Audit History</Title>
          {serialNumber && (
            <Badge color="gray" variant="light">
              S/N: {serialNumber}
            </Badge>
          )}
        </Group>
      }
      size="xl"
    >
      <Stack gap="md">
        {fetchAuditHistory.loading && (
          <Group justify="center" py="xl">
            <Loader size="lg" />
          </Group>
        )}

        {!fetchAuditHistory.loading && auditHistory.length === 0 && (
          <Text c="dimmed" ta="center" py="xl">
            No audit history available for this serial number.
          </Text>
        )}

        {!fetchAuditHistory.loading && auditHistory.length > 0 && (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>User</Table.Th>
                <Table.Th>Action</Table.Th>
                <Table.Th>Field</Table.Th>
                <Table.Th>Old Value</Table.Th>
                <Table.Th>New Value</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {auditHistory.map((audit) => (
                <AuditHistoryRow key={audit.id} audit={audit} />
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Modal>
  );
};